<script setup lang="ts">
import { useI18n } from '@/i18n';
import { isInviteRole } from '@/lib/roles';
import { Alert, Prose } from '@dabble/quillui';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const { context = 'signup' } = defineProps<{
  /** Which auth view hosts the banner — the copy adjusts (sign up vs log in). */
  context?: 'signup' | 'login';
}>();

const route = useRoute();
const { t } = useI18n();

/** Longest title echoed back into the banner; longer values render the generic copy. */
const MAX_TITLE_LENGTH = 60;

/**
 * A template title is a short human label — letters, digits and light
 * punctuation. `?template=` is attacker-supplied and only escaped, so anything
 * outside this set (markup, URLs, control or bidi characters) is treated as a
 * crafted value rather than a title DW3 minted. `/` is excluded so a URL can
 * never render inside the notice.
 */
const TITLE_PATTERN = /^[\p{L}\p{N}\p{M} '‘’"“”\-–—:,.!?&()+#]+$/u;

/**
 * Shown when the visitor arrived from DW3's anonymous template preview ("Use
 * This Template"). The `template` param carries the template's title and is
 * stashed to sessionStorage by `pullSessionFromUrl`, so it survives the
 * cross-origin login round trip — mirrors AuthInviteBanner's `role` handling.
 */
function readQueryOrSession(key: string): string {
  const q = route?.query?.[key];
  // A repeated param arrives as an array whose valueless entries are null.
  const value = Array.isArray(q) ? q.find((entry) => entry !== null && entry !== '') : q;
  if (value !== undefined && value !== null && value !== '') {
    return String(value).trim();
  }
  const s = sessionStorage.getItem(key);
  return s ? String(s).trim() : '';
}

const templateTitle = computed(() => {
  const title = readQueryOrSession('template').replace(/\s+/g, ' ');
  if (!title || title.length > MAX_TITLE_LENGTH || !TITLE_PATTERN.test(title)) return '';
  return title;
});

// The param is present (possibly empty) only on the template funnel, so
// presence — not a usable title — is what gates the banner, in the query and in
// the stashed copy alike. An invite takes precedence, matched to what actually
// makes AuthInviteBanner render so the two never both show, or both stay hidden.
const isTemplateFunnel = computed(() => {
  const query = route?.query;
  return (!!query && 'template' in query) || sessionStorage.getItem('template') !== null;
});
const isInvite = computed(() => isInviteRole(readQueryOrSession('role')));

const heading = computed(() => {
  if (context === 'login') {
    return templateTitle.value
      ? t('template_login_heading', { title: templateTitle.value })
      : t('template_login_heading_generic');
  }
  return templateTitle.value
    ? t('template_signup_heading', { title: templateTitle.value })
    : t('template_signup_heading_generic');
});

const note = computed(() => (context === 'login' ? t('template_login_note') : t('template_signup_note')));
</script>

<template>
  <Alert
    v-if="isTemplateFunnel && !isInvite"
    variant="info"
    icon-left="templates"
    :title="heading"
    class="auth-template-banner"
  >
    <Prose variant="inherit" size="small">{{ note }}</Prose>
  </Alert>
</template>

<style lang="scss" scoped>
.auth-template-banner {
  width: 100%;
  margin-bottom: space('medium');
  text-align: left;
}
</style>
